<template>
    <section class="py-20 bg-[#006CFF]">
        <div class="container mx-auto px-4 text-center">
            <!-- Headline -->
            <h2 class="text-[26px] md:text-4xl lg:text-[32px] font-bold mb-[30px] lg:mb-[50px] text-white mx-auto" v-html="renderTitle()"></h2>

            <!-- Subheadline -->
            <p v-if="subtitle" class="text-[18px] mb-[30px] lg:mb-[50px] text-white max-w-3xl mx-auto font-pro">
                {{ subtitle }}
            </p>

            <!-- CTA Button -->
            <router-link
                v-if="ctaText && ctaLink"
                :to="ctaLink"
                class="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-[#006CFF] transition hover:opacity-90 mb-[30px] lg:mb-12"
                :style="{ background: buttonGradient }"
            >
                {{ ctaText }}
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
            </router-link>

            <!-- Social Proof -->
            <div v-if="showSocialProof" class="flex flex-wrap items-center justify-center gap-8 md:gap-12 mb-[30px] lg:mt-8">
                <!-- Avatar Group with Text -->
                <div v-if="socialProofText && socialProofImage" class="flex items-center gap-3">
                    <div class="flex -space-x-2">
                        <img :src="$asset(socialProofImage)" alt="Customers" class="w-32" />
                    </div>
                    <div class="text-left text-[12px] text-white" v-html="socialProofText"></div>
                </div>

                <!-- Award Badges -->
                <div v-if="badges && badges.length" class="flex flex-row lg:flex-wrap gap-4 md:gap-6 justify-center">
                    <img
                        v-for="(badge, index) in badges"
                        :key="index"
                        :src="$asset(badge.src, 'public')"
                        :alt="badge.alt"
                        class="h-16 md:h-20 object-contain"
                        :class="badge.class"
                    />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
    title: {
        type: String,
        required: true
    },
    titleHighlight: {
        type: String,
        default: ''
    },
    titleHighlightColor: {
        type: String,
        default: '#11D2D4'
    },
    subtitle: {
        type: String,
        default: ''
    },
    ctaText: {
        type: String,
        default: ''
    },
    ctaLink: {
        type: [String, Object],
        default: ''
    },
    buttonGradientFrom: {
        type: String,
        default: '#006CFF'
    },
    buttonGradientTo: {
        type: String,
        default: '#006CFF'
    },
    showSocialProof: {
        type: Boolean,
        default: false
    },
    socialProofText: {
        type: String,
        default: ''
    },
    socialProofImage: {
        type: String,
        default: ''
    },
    badges: {
        type: Array,
        default: () => []
    }
});

const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const parts = props.title.split(props.titleHighlight);

    if (parts.length === 1) return props.title;

    return parts
        .map((part, index) => {
            if (index === 0) return part;

            return `<span style="color: ${props.titleHighlightColor}">${props.titleHighlight}</span>${part}`;
        })
        .join('');
};

const buttonGradient = computed(() => {
    return `linear-gradient(to bottom right, ${props.buttonGradientFrom}, ${props.buttonGradientTo})`;
});
</script>

<style scoped>
/* Additional component-specific styles if needed */
</style>
